import { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PackageModal } from './PackageModal';
import { CsvPackageModal } from './CsvPackageModal';
import {
  ACC_ENV_GUARD_DELETE_NOTE,
  accStopMessage,
  stripAccPrefix,
} from './accAssetMessages';
import { UpdateBudgetModal } from './UpdateBudgetModal';
import { Areas } from '../areas/Areas';
import { v4 as uuidv4 } from 'uuid';
import PackagesAPI from '../../../services/api/admin/packages-api';
import ACCAPI from '../../../services/api/integrations/acc-api';
import Logger from '../../../utils/logger';
import Button from '@mui/material/Button';
import { AuthContext } from '../../context/auth-context';
import { alertActions } from '../../../store/alert';
import ProjectEmptyState from '../../projects/ProjectEmptyState';
import { DataGrid, GridToolbar, GridActionsCellItem } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import DialogContentText from '@mui/material/DialogContentText';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import SyncIcon from '@mui/icons-material/Sync';

// Ticket #257: how long to follow a background ACC asset run. Large imports
// create thousands of assets, so this is longer than the ACC sync wait.
const ACC_ASSETS_POLL_TIMEOUT_MS = 60 * 60 * 1000;

// How often to re-check a running sync, when to stop waiting on one that never
// reports finishing, and how many failed checks in a row end the wait.
const SYNC_POLL_INTERVAL_MS = 5000;
const SYNC_POLL_TIMEOUT_MS = 15 * 60 * 1000;
const SYNC_POLL_MAX_CONSECUTIVE_ERRORS = 3;

// Autodesk token failures (missing, expired, revoked or unrefreshable) as they
// surface from requireAutodeskAuth, authService and the ACC client.
const ACC_AUTH_ERROR_PATTERN =
  /authentication failed|autodesk authentication|autodesk token|access token|refresh token|invalid_grant|authenticate first|re-authenticate|unauthorized|status code 401/i;

const ACC_AUTH_ERROR_MESSAGE =
  'The connection to Autodesk has expired. Please sign in again ("Autodesk Sign In" in the user menu), then run Sync Assets.';

// Turns a recorded sync error into something an admin can act on. The
// orchestrator joins one "service: error" entry per failed step; the first is
// the root cause and the rest usually repeat it.
const describeAccSyncError = (rawError) => {
  if (!rawError) return 'No reason was recorded.';
  if (ACC_AUTH_ERROR_PATTERN.test(rawError)) return ACC_AUTH_ERROR_MESSAGE;
  return rawError.split('; ')[0];
};

export const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [currentPackage, setCurrentPackage] = useState('');
  const [open, setOpen] = useState(false);
  const [editPackage, setEditPackage] = useState(false);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const [csvMenuAnchor, setCsvMenuAnchor] = useState(null);
  const csvMenuOpen = Boolean(csvMenuAnchor);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  // Package awaiting delete confirmation: { pkg: { id, name, autodesk_asset_id },
  // afterZeroBudget } — afterZeroBudget when Update Budget just zeroed it.
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedPackageForBudget, setSelectedPackageForBudget] =
    useState(null);
  const [resolutionModal, setResolutionModal] = useState({
    open: false,
    type: '', // 'confirm' or 'clear'
    packageData: null,
    notes: '',
  });
  const [autodeskSyncWarningOpen, setAutodeskSyncWarningOpen] = useState(false);
  // Replaces the dialog's default text when a package's ACC asset could not be
  // created (ticket #257).
  const [autodeskSyncWarningMessage, setAutodeskSyncWarningMessage] =
    useState(null);
  const [assetSyncSubmitted, setAssetSyncSubmitted] = useState(false);
  const [accSyncStatus, setAccSyncStatus] = useState(null);
  const [accSyncStatusUnavailable, setAccSyncStatusUnavailable] =
    useState(false);
  const [packagesRefreshKey, setPackagesRefreshKey] = useState(0);
  // Ticket #257: a background run is creating this project's pending ACC assets
  // (started by a CSV import or the "Create pending ACC assets" button).
  const [accAssetsRunning, setAccAssetsRunning] = useState(false);

  const auth = useContext(AuthContext);
  const dispatch = useDispatch();

  // Global project selection from Redux (set via the sidebar drawer).
  const currentProject = useSelector((state) => state.project.selectedProject);

  // Load this project's ACC sync status for the readout next to the button.
  // A submitted sync belongs to the project it was fired for, so the flag is
  // cleared here too — otherwise the button stays disabled after switching.
  // Admin-only: the endpoint requires admin.
  useEffect(() => {
    setAssetSyncSubmitted(false);
    setAccSyncStatus(null);
    setAccSyncStatusUnavailable(false);

    if (!currentProject?.id || !auth.token || !auth.isAdmin) {
      return;
    }

    let cancelled = false;

    const getSyncStatus = async () => {
      try {
        const status = await ACCAPI.getProjectSyncStatus(
          currentProject.id,
          auth.token
        );
        if (!cancelled) {
          setAccSyncStatus(status);
        }
      } catch (err) {
        // Don't interrupt the packages workflow, but show that the status
        // couldn't be loaded rather than letting the readout disappear.
        Logger.error('Error getting ACC sync status', err, {
          projectId: currentProject.id,
        });
        if (!cancelled) {
          setAccSyncStatusUnavailable(true);
        }
      }
    };

    getSyncStatus();

    return () => {
      cancelled = true;
    };
  }, [currentProject?.id, auth.token, auth.isAdmin]);

  const accSyncInFlight =
    assetSyncSubmitted || accSyncStatus?.isSyncRunning === true;

  const thisProjectSyncing =
    assetSyncSubmitted ||
    (accSyncStatus?.lastSyncStatus === 'in_progress' &&
      accSyncStatus?.isSyncRunning === true);
  const otherSyncRunning = accSyncInFlight && !thisProjectSyncing;

  // While a sync is in flight, re-check the status until it clears, so the
  // readout and the button settle on their own instead of needing a refresh.
  useEffect(() => {
    if (
      !accSyncInFlight ||
      !currentProject?.id ||
      !auth.token ||
      !auth.isAdmin
    ) {
      return;
    }

    // Whether we were the ones who started it — decides if a toast is warranted
    // when it lands. Another admin's sync finishing shouldn't interrupt us.
    const startedByUs = assetSyncSubmitted;
    const startedAt = Date.now();

    let cancelled = false;
    let timeoutId;

    let consecutiveErrors = 0;

    // Stop waiting and hand the button back. The stale isSyncRunning has to be
    // cleared too, or it keeps the button disabled on its own.
    const stopWaiting = (message) => {
      setAssetSyncSubmitted(false);
      setAccSyncStatus((prev) =>
        prev ? { ...prev, isSyncRunning: false } : prev
      );
      if (startedByUs) {
        setSnackbar({
          open: true,
          message,
          severity: 'warning',
          autoHideDuration: 10000,
        });
      }
    };

    const checkAgain = () => {
      if (cancelled) return;

      if (Date.now() - startedAt > SYNC_POLL_TIMEOUT_MS) {
        Logger.warn('Gave up waiting on ACC sync to finish', {
          projectId: currentProject.id,
        });
        stopWaiting(
          `Still no result after ${SYNC_POLL_TIMEOUT_MS / 60000} minutes, so this page stopped waiting. The sync may still be running — refresh the page later to check its status.`
        );
        return;
      }

      timeoutId = setTimeout(poll, SYNC_POLL_INTERVAL_MS);
    };

    const poll = async () => {
      if (cancelled) return;

      try {
        const status = await ACCAPI.getProjectSyncStatus(
          currentProject.id,
          auth.token
        );
        if (cancelled) return;
        consecutiveErrors = 0;

        // Lock still held: the sync is still going.
        if (status.isSyncRunning) {
          checkAgain();
          return;
        }

        // Finished. Publish the result and let the button come back.
        setAccSyncStatus(status);
        setAssetSyncSubmitted(false);
        // The sync rewrote this project's ACC data, so re-pull the table.
        setPackagesRefreshKey((key) => key + 1);

        if (startedByUs) {
          const outcome = status.lastSyncStatus;
          if (outcome === 'success') {
            setSnackbar({
              open: true,
              message: 'Asset sync finished. Packages have been refreshed.',
              severity: 'success',
              autoHideDuration: 5000,
            });
          } else if (outcome === 'failed') {
            dispatch(
              alertActions.showAlert({
                title: ACC_AUTH_ERROR_PATTERN.test(status.lastSyncError || '')
                  ? 'Autodesk connection expired'
                  : 'Asset sync failed',
                message: describeAccSyncError(status.lastSyncError),
              })
            );
          } else {
            // The lock is free but no final result was written (the row still
            // says in_progress): the sync died mid-run, e.g. a server restart.
            dispatch(
              alertActions.showAlert({
                title: 'Asset sync stopped',
                message:
                  'The asset sync stopped before it finished (the server may have restarted). Run Sync Assets again.',
              })
            );
          }
        }
      } catch (err) {
        Logger.error('Error checking ACC sync status', err, {
          projectId: currentProject.id,
        });
        // Ride out a blip, but a run of failures means the sync can't be
        // tracked any more — say so instead of silently waiting out the timeout.
        consecutiveErrors += 1;
        if (consecutiveErrors >= SYNC_POLL_MAX_CONSECUTIVE_ERRORS) {
          const reason = err.response?.data?.message || err.message;
          stopWaiting(
            `Couldn't check on the asset sync (${reason}). It may still be running — refresh the page to see its status.`
          );
          return;
        }
        checkAgain();
      }
    };

    timeoutId = setTimeout(poll, SYNC_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [accSyncInFlight, currentProject?.id, auth.token, auth.isAdmin, dispatch]);

  // Fetch this project's packages whenever the global project changes.
  useEffect(() => {
    if (!currentProject?.id || !auth.token) {
      setPackages([]);
      return;
    }

    const getPackages = async () => {
      try {
        const projectPackages = await PackagesAPI.getByProject(
          currentProject.id,
          auth.token
        );
        Logger.info('Packages retrieved for project', {
          projectId: currentProject.id,
          count: projectPackages?.length,
        });
        setPackages(projectPackages);
      } catch (err) {
        Logger.error('Error getting packages for project', err, {
          projectId: currentProject.id,
        });
        const errorMessage = err.response?.data?.message || err.message;
        alert(errorMessage);
      }
    };

    getPackages();
  }, [currentProject?.id, auth.token, packagesRefreshKey]);

  // Ticket #257: on opening a project, pick up an ACC asset run that is already
  // going (e.g. a CSV import whose modal was closed), so the button waits for it.
  // Admin-only, like the endpoint.
  useEffect(() => {
    setAccAssetsRunning(false);
    if (!currentProject?.id || !auth.token || !auth.isAdmin) return;

    let cancelled = false;
    PackagesAPI.getAccAssetStatus(currentProject.id, auth.token)
      .then((status) => {
        if (!cancelled && status.running) setAccAssetsRunning(true);
      })
      .catch((err) => {
        Logger.error('Error getting ACC asset status', err, {
          projectId: currentProject.id,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [currentProject?.id, auth.token, auth.isAdmin]);

  // While an ACC asset run is going, re-check it until it finishes, then reload
  // the packages so their pending/failed chips update, and report the outcome.
  useEffect(() => {
    if (!accAssetsRunning || !currentProject?.id || !auth.token) return;

    const startedAt = Date.now();
    let cancelled = false;
    let timeoutId;

    const finish = (message, severity) => {
      setAccAssetsRunning(false);
      setPackagesRefreshKey((key) => key + 1);
      if (message) {
        setSnackbar({ open: true, message, severity, autoHideDuration: 10000 });
      }
    };

    const poll = async () => {
      if (cancelled) return;
      try {
        const status = await PackagesAPI.getAccAssetStatus(
          currentProject.id,
          auth.token
        );
        if (cancelled) return;
        if (!status.running) {
          const progress = status.progress;
          const stopped = accStopMessage(progress);
          const parts = progress
            ? [
                `${progress.created} created`,
                progress.linked ? `${progress.linked} already in ACC` : null,
                progress.failed ? `${progress.failed} failed` : null,
              ].filter(Boolean)
            : [];
          finish(
            `ACC assets: ${parts.join(', ') || 'finished'}.` +
              (stopped ? ` Stopped early: ${stopped}` : ''),
            stopped || progress?.failed ? 'warning' : 'success'
          );
          return;
        }
      } catch (err) {
        // A missed status call does not stop the run; keep checking.
        Logger.error('Error checking ACC asset status', err, {
          projectId: currentProject.id,
        });
      }
      if (Date.now() - startedAt > ACC_ASSETS_POLL_TIMEOUT_MS) {
        Logger.warn('Gave up waiting on ACC asset run to finish', {
          projectId: currentProject.id,
        });
        finish(null);
        return;
      }
      timeoutId = setTimeout(poll, SYNC_POLL_INTERVAL_MS);
    };

    timeoutId = setTimeout(poll, SYNC_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [accAssetsRunning, currentProject?.id, auth.token]);

  const accAssetsWaiting = packages.filter((p) => p.acc_sync_state).length;

  const handleCreatePendingAccAssets = async () => {
    Logger.userAction('Creating pending ACC assets', {
      projectId: currentProject.id,
    });
    setAccAssetsRunning(true);
    try {
      const result = await PackagesAPI.retryAccAssets(
        currentProject.id,
        auth.token
      );
      if (!result.started) {
        // Nothing waiting after all — the grid was out of date.
        setAccAssetsRunning(false);
        setPackagesRefreshKey((key) => key + 1);
        setSnackbar({
          open: true,
          message: 'No packages are waiting for an ACC asset.',
          severity: 'info',
        });
        return;
      }
      setSnackbar({
        open: true,
        message:
          'Creating ACC assets in the background. This page will update when it finishes.',
        severity: 'success',
      });
    } catch (err) {
      Logger.error('Error starting ACC asset creation', err, {
        projectId: currentProject.id,
      });
      const alreadyRunning =
        err.response?.data?.error?.reason === 'acc_asset_worker_running';
      if (alreadyRunning) {
        // Follow the run that is already going instead.
        setSnackbar({
          open: true,
          message:
            'ACC assets are already being created for this project. This page will update when it finishes.',
          severity: 'warning',
        });
        return;
      }
      setAccAssetsRunning(false);
      setSnackbar({
        open: true,
        message:
          err.response?.data?.message ||
          err.message ||
          'Failed to start creating ACC assets',
        severity: 'error',
      });
    }
  };

  const handleOpen = () => {
    setEditPackage(false);
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    setCurrentPackage('');
  };

  const handleCsvUploadOpen = () => {
    setCsvUploadOpen(true);
  };

  const handleCsvUploadClose = () => {
    setCsvUploadOpen(false);
  };

  const handleCsvMenuOpen = (event) => {
    setCsvMenuAnchor(event.currentTarget);
  };

  const handleCsvMenuClose = () => {
    setCsvMenuAnchor(null);
  };

  const handleDownloadTemplate = async () => {
    handleCsvMenuClose();
    try {
      const response = await PackagesAPI.downloadCsvTemplate(
        currentProject.id,
        auth.token
      );
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'packages_create_template.csv';
      link.click();
      window.URL.revokeObjectURL(url);
      setSnackbar({
        open: true,
        message: 'Blank template downloaded successfully',
        severity: 'success',
      });
    } catch (err) {
      Logger.error('Error downloading template CSV', err);
      alert('Error downloading template: ' + err.message);
    }
  };

  const handleDownloadExisting = async () => {
    handleCsvMenuClose();
    try {
      const response = await PackagesAPI.downloadPackagesCsv(
        currentProject.id,
        auth.token
      );
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      let filename = `packages_${currentProject?.name?.replace(/[^a-z0-9]/gi, '_') || 'project'}.csv`;
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('attachment') !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      setSnackbar({
        open: true,
        message: 'Existing packages downloaded successfully',
        severity: 'success',
      });
    } catch (err) {
      Logger.error('Error downloading existing packages CSV', err);
      alert('Error downloading packages CSV: ' + err.message);
    }
  };

  const handleUploadCsvClick = () => {
    handleCsvMenuClose();
    handleCsvUploadOpen();
  };

  const formatAssetSyncStatus = () => {
    if (thisProjectSyncing) {
      return { text: 'ACC Syncing In Progress', color: '#ff9800' };
    }

    if (!accSyncStatus) {
      return accSyncStatusUnavailable
        ? { text: 'Last ACC Sync: status unavailable', color: '#666' }
        : null;
    }

    const status = accSyncStatus.lastSyncStatus;
    const lastSyncAt = accSyncStatus.lastSyncAt;
    const error = accSyncStatus.lastSyncError;
    const last = (text) => `Last ACC Sync: ${text}`;

    if (!status && !lastSyncAt) {
      return { text: last('Never synced'), color: '#666' };
    }

    if (status === 'in_progress') {
      return {
        text: last('Interrupted - never finished'),
        color: '#d32f2f',
      };
    }

    if (status === 'success') {
      const date = lastSyncAt ? new Date(lastSyncAt).toLocaleString() : '';
      return { text: last(`Success - ${date}`), color: '#4caf50' };
    }

    if (status === 'failed') {
      const date = lastSyncAt ? new Date(lastSyncAt).toLocaleString() : '';
      const reason = error
        ? ACC_AUTH_ERROR_PATTERN.test(error)
          ? 'Autodesk connection expired'
          : error.split('; ')[0]
        : '';
      const errorMsg =
        reason.length > 50 ? reason.substring(0, 50) + '...' : reason;
      return {
        text: last(`Failed - ${date}${errorMsg ? ` (${errorMsg})` : ''}`),
        color: '#d32f2f',
      };
    }

    return { text: last(status || 'Unknown'), color: '#666' };
  };

  const handleSyncAssets = async () => {
    Logger.userAction('Triggering ACC assets sync', {
      projectId: currentProject.id,
    });
    setAssetSyncSubmitted(true);

    try {
      await ACCAPI.syncProjectAssets(currentProject.id, auth.token);
      Logger.info('Asset sync submitted successfully', {
        projectId: currentProject.id,
      });
      setSnackbar({
        open: true,
        message:
          'Asset sync started. This page will update on its own when it finishes.',
        severity: 'success',
      });
    } catch (err) {
      Logger.error('Error triggering asset sync', err, {
        projectId: currentProject.id,
      });

      const data = err.response?.data;
      const alreadyRunning = data?.error?.reason === 'sync_already_running';
      // requireAutodeskAuth rejects with a 401 carrying an authUrl when the
      // Autodesk token is missing or can't be refreshed. It sends no `message`,
      // so this used to surface as axios's bare "Request failed with status code 401".
      const autodeskAuthFailed =
        err.response?.status === 401 && Boolean(data?.authUrl);

      let title;
      let message;
      if (alreadyRunning) {
        title = 'Sync already in progress';
        message =
          'A sync is already in progress. Please wait for it to finish before retrying.';
      } else if (autodeskAuthFailed) {
        title = 'Autodesk connection expired';
        message = ACC_AUTH_ERROR_MESSAGE;
      } else if (!err.response) {
        title = "Couldn't reach the server";
        message =
          "Couldn't reach the server to start the asset sync. Check your connection and try again.";
      } else {
        // App routes put the reason in `message`; the Autodesk middleware uses `error`.
        title = "Couldn't start the asset sync";
        message =
          data?.message ||
          (typeof data?.error === 'string' ? data.error : null) ||
          err.message;
      }

      dispatch(alertActions.showAlert({ title, message }));

      // Nothing was started, so re-enable the button for an immediate retry.
      setAssetSyncSubmitted(false);
    }
  };

  const handleAreasOpen = () => {
    setAreasOpen(true);
  };

  const handleAreasClose = () => {
    setAreasOpen(false);
    // Refresh package categories/areas when closing the areas modal
    // This will be handled by PackageModal when it reopens
  };

  const handleCsvUploadComplete = async (result) => {
    // Refresh the packages list after successful CSV upload
    if (currentProject) {
      try {
        const projectPackages = await PackagesAPI.getByProject(
          currentProject.id,
          auth.token
        );
        setPackages(projectPackages);
      } catch (err) {
        Logger.error('Error refreshing packages after CSV upload', err);
      }
    }

    // Surface a result toast (and the ACC sync warning if any field failed to
    // sync), matching the package edit flow.
    if (result) {
      const parts = [];
      if (result.created) parts.push(`${result.created} created`);
      if (result.updated) parts.push(`${result.updated} updated`);
      if (result.skipped) parts.push(`${result.skipped} skipped`);
      // Ticket #257: follow the background ACC asset run so the grid reloads
      // when it finishes.
      const accAssetsStarted =
        result.acc_assets?.pending > 0 &&
        result.acc_assets.worker !== 'not_started';
      if (accAssetsStarted) setAccAssetsRunning(true);
      setSnackbar({
        open: true,
        message:
          (parts.length
            ? `CSV import complete: ${parts.join(', ')}`
            : 'CSV import complete') +
          (accAssetsStarted
            ? '. ACC assets are being created in the background.'
            : ''),
        severity: 'success',
      });

      if (result.acc_sync_failed > 0) {
        setAutodeskSyncWarningMessage(null);
        setAutodeskSyncWarningOpen(true);
      }
    }
  };

  // Ticket #257: reflect the saved "Sync to ACC" state in the grid, and warn
  // when the package's ACC asset could not be created.
  const applyAccAssetSyncResult = (packageId, savedPackage) => {
    const accAssetSync = savedPackage?.accAssetSync;
    if (!accAssetSync) return;
    setPackages((prevPackages) =>
      prevPackages.map((item) =>
        item.id === packageId
          ? {
              ...item,
              acc_sync_enabled: accAssetSync.accSyncEnabled,
              autodesk_asset_id: accAssetSync.autodeskAssetId,
              // pending/failed chip; unknown (undefined) leaves it as it was
              ...(accAssetSync.accSyncState !== undefined && {
                acc_sync_state: accAssetSync.accSyncState,
                acc_sync_error:
                  accAssetSync.accSyncState === 'failed'
                    ? item.acc_sync_error
                    : null,
              }),
            }
          : item
      )
    );
    if (accAssetSync.failed) {
      setAutodeskSyncWarningMessage(
        `The package was saved, but it could not be synced to Autodesk ` +
          `Construction Cloud: ${accAssetSync.error || 'unknown error'} ` +
          `"Sync to ACC" has been turned off for this package. Once the ` +
          `problem is fixed, edit the package and turn it back on to retry.`
      );
      setAutodeskSyncWarningOpen(true);
    }
  };

  const handleAddPackage = async (addedPackage) => {
    const id = uuidv4();
    const newPackage = {
      id: id,
      name: addedPackage.name,
      area_id: addedPackage.area.id,
      area: addedPackage.area.name,
      archived: addedPackage.archived,
      tracking_status: addedPackage.tracking_status.name,
      current_tracking_status_id: addedPackage.tracking_status.id,
      status_step_set_id: addedPackage.status_step_set_id || null,
      project_id: currentProject.id,
      field_cost_code: addedPackage.field_cost_code,
      field_phase: addedPackage.field_phase,
      units: addedPackage.units,
      uom: addedPackage.uom,
      trades: addedPackage.trade,
      trade: addedPackage.trade,
      trade_id: addedPackage.trade_id,
      default_activity_id: addedPackage.default_activity_id,
      activity_ids: addedPackage.activity_ids || [],
      include_activities: addedPackage.include_activities !== false,
      requires_shop_fabrication:
        addedPackage.requires_shop_fabrication || false,
      requires_vdc_design: addedPackage.requires_vdc_design || false,
      is_general_conditions: addedPackage.is_general_conditions || false,
      is_change_order: addedPackage.is_change_order || false,
      shop_phase: addedPackage.shop_phase || '',
      shop_cost_code: addedPackage.shop_cost_code || '',
      autodesk_name: addedPackage.autodesk_name || null,
      acc_sync_enabled: addedPackage.acc_sync_enabled === true,
    };
    Logger.userAction('Adding new package', { package: newPackage });

    const newPackageList = [...packages, newPackage];
    const sortedPackageList = newPackageList.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : a.name.toLowerCase() < b.name.toLowerCase()
          ? -1
          : 0
    );
    setPackages(sortedPackageList);

    setCurrentPackage('');

    setOpen(false);

    try {
      const addedPackage = await PackagesAPI.create(newPackage, auth.token);
      Logger.info('Package added successfully', { package: addedPackage });
      applyAccAssetSyncResult(newPackage.id, addedPackage);
    } catch (err) {
      Logger.error('Error adding package', err);
      alert(err.response?.data?.message || err.message);
      setPackages(packages.filter((item) => item.id !== newPackage.id));
    }
  };

  /**
   * Delete an already-confirmed package: hide its row, then put it back if the
   * server refuses (tracked time, budgeted hours, or the ACC delete failed —
   * nothing changes on the server in those cases).
   * @returns {Promise<boolean>} true when the package was deleted
   */
  const deletePackageRow = async (pkg) => {
    Logger.userAction('Deleting package', { packageId: pkg.id });
    const oldPackages = packages;
    setPackages(packages.filter((item) => item.id !== pkg.id));

    try {
      const { accSyncResult } = await PackagesAPI.delete(pkg.id, auth.token);
      Logger.info('Package deleted successfully', { packageId: pkg.id });
      // Dev/staging block outbound ACC writes, so a linked asset stays live in
      // ACC there — say so rather than implying it was archived.
      setSnackbar(
        accSyncResult?.reason === 'env_guard'
          ? {
              open: true,
              message: `Package "${pkg.name}" deleted. ${ACC_ENV_GUARD_DELETE_NOTE}`,
              severity: 'warning',
              autoHideDuration: 8000,
            }
          : {
              open: true,
              message: `Package "${pkg.name}" deleted`,
              severity: 'success',
            }
      );
      return true;
    } catch (err) {
      Logger.error('Error deleting package in backend', err);
      setPackages(oldPackages);
      // A modal, not a toast: the reason (e.g. budget left, reconnect ACC)
      // tells the admin what to do next, so it stays until dismissed.
      dispatch(
        alertActions.showAlert({
          title: `Package "${pkg.name}" was not deleted`,
          message: err.response?.data?.message || err.message,
        })
      );
      return false;
    }
  };

  const accArchiveNote = (pkg) =>
    pkg?.autodesk_asset_id ? 'Its ACC asset will be archived as well.' : '';

  const handleDeletePackage = (rowSelected) => {
    const { id, name, autodesk_asset_id } = rowSelected.row;
    setDeleteConfirm({
      pkg: { id, name, autodesk_asset_id },
      afterZeroBudget: false,
    });
  };

  // Runs when the delete confirm is accepted — from the Delete action or the
  // prompt after Update Budget brings a budget to zero (ticket #268).
  const handleConfirmDelete = async () => {
    const { pkg } = deleteConfirm;
    setDeleteConfirm(null);
    const deleted = await deletePackageRow(pkg);
    // Update Budget can be opened from Edit Package; that modal now shows a
    // package that no longer exists.
    if (deleted && open && currentPackage?.id === pkg.id) {
      handleCancel();
    }
  };

  const handleEditPackage = (rowSelected) => {
    const item = rowSelected.row;
    setCurrentPackage({
      id: item.id,
      name: item.name,
      area_id: item.area_id,
      area: item.area,
      archived: item.archived,
      tracking_status: item.tracking_status,
      current_tracking_status_id: item.current_tracking_status_id,
      status_step_set_id: item.status_step_set_id || null,
      project_id: currentProject.id,
      field_cost_code: item.field_cost_code,
      field_phase: item.field_phase,
      units: item.units,
      uom: item.uom,
      trades: item.trades,
      trade: item.trades,
      trade_id: item.trade_id,
      default_activity_id: item.default_activity_id,
      activity_ids: item.activity_ids || [],
      include_activities: item.include_activities !== false,
      requires_shop_fabrication: item.requires_shop_fabrication || false,
      requires_vdc_design: item.requires_vdc_design || false,
      is_general_conditions: item.is_general_conditions || false,
      is_change_order: item.is_change_order || false,
      shop_phase: item.shop_phase || '',
      shop_cost_code: item.shop_cost_code || '',
      autodesk_asset_id: item.autodesk_asset_id || null,
      autodesk_project_id: item.autodesk_project_id || null,
      autodesk_last_synced: item.autodesk_last_synced || null,
      autodesk_name: item.autodesk_name || '',
      acc_sync_enabled: item.acc_sync_enabled === true,
      field_hrs_budgeted: item.field_hrs_budgeted,
      shop_hrs_budgeted: item.shop_hrs_budgeted,
      total_hrs: item.total_hrs,
    });
    Logger.userAction('Editing package', { package: item });
    setEditPackage(true);
    setOpen(true);
  };

  const handleUpdatePackage = async (editedPackage) => {
    Logger.userAction('Updating package', { package: editedPackage });

    // Find the existing package to preserve trade name if not provided in editedPackage
    const existingPackage = packages.find((pkg) => pkg.id === editedPackage.id);
    const tradeName =
      editedPackage.trade ||
      existingPackage?.trades ||
      existingPackage?.trade ||
      '';

    const updatedPackage = {
      id: editedPackage.id,
      name: editedPackage.name,
      area_id: editedPackage.area.id,
      area: editedPackage.area.name,
      archived: editedPackage.archived,
      tracking_status: editedPackage.tracking_status.name,
      current_tracking_status_id: editedPackage.tracking_status.id,
      status_step_set_id: editedPackage.status_step_set_id || null,
      project_id: currentProject.id,
      field_cost_code: editedPackage.field_cost_code,
      field_phase: editedPackage.field_phase,
      units: editedPackage.units,
      uom: editedPackage.uom,
      trades: tradeName,
      trade: tradeName,
      trade_id: editedPackage.trade_id,
      default_activity_id: editedPackage.default_activity_id,
      activity_ids: editedPackage.activity_ids || [],
      include_activities: editedPackage.include_activities !== false,
      requires_shop_fabrication:
        editedPackage.requires_shop_fabrication || false,
      requires_vdc_design: editedPackage.requires_vdc_design || false,
      is_general_conditions: editedPackage.is_general_conditions || false,
      is_change_order: editedPackage.is_change_order || false,
      shop_phase: editedPackage.shop_phase || '',
      shop_cost_code: editedPackage.shop_cost_code || '',
      autodesk_name: editedPackage.autodesk_name || '',
      acc_sync_enabled: editedPackage.acc_sync_enabled === true,
      autodesk_asset_id: existingPackage?.autodesk_asset_id || null,
      // Not edited in the modal; the save result updates them if they change.
      acc_sync_state: existingPackage?.acc_sync_state ?? null,
      acc_sync_error: existingPackage?.acc_sync_error ?? null,
    };

    setOpen(false);

    const previousPackages = packages;

    const updatedRow = { ...existingPackage, ...updatedPackage };

    const updatedPackages = packages.map((item) => {
      if (item.id === updatedPackage.id) {
        return updatedRow;
      } else {
        return item;
      }
    });

    setPackages(updatedPackages);

    try {
      const modifiedPackage = await PackagesAPI.update(
        updatedPackage,
        auth.token
      );
      Logger.info('Package updated successfully', { package: modifiedPackage });

      // Update the trade name from API response if available
      const tradeNameFromApi =
        modifiedPackage.trade_name || modifiedPackage.trades;
      if (tradeNameFromApi) {
        // Update the packages list with the correct trade name from API
        setPackages((prevPackages) =>
          prevPackages.map((item) =>
            item.id === updatedPackage.id
              ? {
                  ...updatedRow,
                  trades: tradeNameFromApi,
                  trade: tradeNameFromApi,
                }
              : item
          )
        );
      }

      const numTimecardsUpdated = modifiedPackage.numTimecards || 0;

      if (numTimecardsUpdated > 0) {
        alert(
          `There were ${numTimecardsUpdated} timecard entries updated with the new phase and cost code`
        );
      }

      // Show Autodesk sync result or just success message
      const autodeskSync = modifiedPackage.autodeskSync;
      Logger.debug('Autodesk sync result', { autodeskSync });

      if (autodeskSync) {
        if (autodeskSync.blocked) {
          setSnackbar({
            open: true,
            message:
              'Package updated (Autodesk sync intentionally skipped in this environment)',
            severity: 'success',
          });
        } else if (autodeskSync.success) {
          setSnackbar({
            open: true,
            message: 'Package updated and synced to Autodesk',
            severity: 'success',
          });
        } else {
          setSnackbar({
            open: true,
            message: `Package updated, but Autodesk sync failed: ${autodeskSync.error || 'Unknown error'}`,
            severity: 'warning',
          });
        }
      } else {
        // No Autodesk sync was attempted (status didn't change or package not from ACC)
        setSnackbar({
          open: true,
          message: 'Package updated successfully',
          severity: 'success',
        });
      }

      applyAccAssetSyncResult(updatedPackage.id, modifiedPackage);
      if (
        modifiedPackage.accSyncFailed &&
        !modifiedPackage.accAssetSync?.failed
      ) {
        setAutodeskSyncWarningMessage(null);
        setAutodeskSyncWarningOpen(true);
      }
    } catch (err) {
      Logger.error('Error updating package', err);

      // Extract error details from response
      const errorResponse = err.response?.data;
      const errorMessage = errorResponse?.message || err.message;
      const errorType = errorResponse?.error?.type;
      const requiresReauth = errorResponse?.error?.requiresReauth;
      const statusCode = err.response?.status;

      const isAuthError =
        requiresReauth ||
        statusCode === 401 ||
        errorType === 'AutodeskAuthError' ||
        errorMessage.toLowerCase().includes('sign in') ||
        errorMessage.toLowerCase().includes('session') ||
        errorMessage.toLowerCase().includes('autodesk') ||
        errorMessage.toLowerCase().includes('permission');

      dispatch(
        alertActions.showAlert({
          title: isAuthError
            ? 'Error updating package status: Autodesk Authentication Required'
            : 'There was an error updating the package status',
          message: isAuthError
            ? "Your Autodesk session has expired or you don't have permission. Please sign in to Autodesk again."
            : errorMessage,
        })
      );
      setPackages(previousPackages);
    }
  };

  const handleOpenConfirmDeleteModal = (row) => {
    setResolutionModal({
      open: true,
      type: 'confirm',
      packageData: row,
      notes: '',
    });
  };

  const handleOpenClearPendingModal = (row) => {
    setResolutionModal({
      open: true,
      type: 'clear',
      packageData: row,
      notes: '',
    });
  };

  const handleCloseResolutionModal = () => {
    setResolutionModal({
      open: false,
      type: '',
      packageData: null,
      notes: '',
    });
  };

  const handleResolveDeletion = async () => {
    const { type, packageData, notes } = resolutionModal;
    if (!packageData) return;

    try {
      if (type === 'confirm') {
        Logger.userAction('Confirming Autodesk deletion', {
          packageId: packageData.id,
          notes,
        });
        await PackagesAPI.confirmAutodeskDeletion(
          packageData.id,
          auth.token,
          notes
        );

        // Remove from current local packages list since it's now deleted/hidden
        setPackages((prev) => prev.filter((p) => p.id !== packageData.id));
        setSnackbar({
          open: true,
          message: 'Package deletion confirmed successfully',
          severity: 'success',
        });
      } else if (type === 'clear') {
        Logger.userAction('Clearing Autodesk deletion warning', {
          packageId: packageData.id,
          notes,
        });
        await PackagesAPI.clearAutodeskDeletionPending(
          packageData.id,
          auth.token,
          notes
        );

        // Update local package state
        setPackages((prev) =>
          prev.map((p) =>
            p.id === packageData.id
              ? {
                  ...p,
                  autodesk_delete_pending: false,
                  autodesk_delete_pending_at: null,
                  autodesk_delete_pending_reason: null,
                }
              : p
          )
        );
        setSnackbar({
          open: true,
          message: 'Autodesk deletion warning cleared successfully',
          severity: 'success',
        });
      }
    } catch (err) {
      Logger.error('Error resolving deletion warning', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || err.message,
        severity: 'error',
      });
    } finally {
      handleCloseResolutionModal();
    }
  };

  const handleUnarchivePackage = async (row) => {
    if (
      window.confirm(
        `Are you sure you want to unarchive the package "${row.name}"?`
      )
    ) {
      try {
        Logger.userAction('Unarchiving package', { packageId: row.id });
        await PackagesAPI.unarchive(row.id, auth.token);

        // Update local package state
        setPackages((prev) =>
          prev.map((p) => (p.id === row.id ? { ...p, archived: false } : p))
        );
        setSnackbar({
          open: true,
          message: 'Package unarchived successfully',
          severity: 'success',
        });
      } catch (err) {
        Logger.error('Error unarchiving package', err);
        setSnackbar({
          open: true,
          message: err.response?.data?.message || err.message,
          severity: 'error',
        });
      }
    }
  };

  // Handler for when trade is auto-copied from project to package
  const handleTradeAutoCopied = (packageId, tradeName) => {
    Logger.debug('Trade auto-copied for package', { packageId, tradeName });
    setPackages((prevPackages) =>
      prevPackages.map((pkg) =>
        pkg.id === packageId
          ? { ...pkg, trades: tradeName, trade: tradeName }
          : pkg
      )
    );
  };

  // Handler for when activities are auto-copied to package
  const handleActivitiesAutoCopied = (packageId, activities) => {
    Logger.debug('Activities auto-copied for package', {
      packageId,
      activities,
    });
    setPackages((prevPackages) =>
      prevPackages.map((pkg) =>
        pkg.id === packageId ? { ...pkg, activities: activities } : pkg
      )
    );
  };

  const handleOpenUpdateBudget = (rowSelected) => {
    setSelectedPackageForBudget(rowSelected.row);
    setBudgetModalOpen(true);
  };

  const handleOpenUpdateBudgetFromEditPackage = (pkg) => {
    setSelectedPackageForBudget(pkg);
    setBudgetModalOpen(true);
  };

  const handleCloseUpdateBudget = () => {
    setSelectedPackageForBudget(null);
    setBudgetModalOpen(false);
  };

  const handleUpdateBudgetComplete = (updatedPackage) => {
    setPackages((prevPackages) =>
      prevPackages.map((pkg) =>
        pkg.id === updatedPackage.id
          ? {
              ...pkg,
              field_hrs_budgeted: updatedPackage.field_hrs_budgeted,
              shop_hrs_budgeted: updatedPackage.shop_hrs_budgeted,
              total_hrs: updatedPackage.total_hrs,
              earned_field_hrs: updatedPackage.earned_field_hrs,
              earned_shop_hrs: updatedPackage.earned_shop_hrs,
              total_earned_hrs: updatedPackage.total_earned_hrs,
            }
          : pkg
      )
    );
    setCurrentPackage((prev) => {
      if (prev && prev.id === updatedPackage.id) {
        return {
          ...prev,
          field_hrs_budgeted: updatedPackage.field_hrs_budgeted,
          shop_hrs_budgeted: updatedPackage.shop_hrs_budgeted,
          total_hrs: updatedPackage.total_hrs,
        };
      }
      return prev;
    });
    setSnackbar({
      open: true,
      message: 'Package budget updated successfully',
      severity: 'success',
    });

    // Only a save that took the budget to zero — not one that was already at
    // zero, and not a partial reduction.
    if (
      Number(updatedPackage.previous_total_hrs) > 0 &&
      Number(updatedPackage.total_hrs) === 0
    ) {
      // Budgeted hours block deletion, so zeroing them is usually the step
      // right before it — offer it now.
      const row = packages.find((pkg) => pkg.id === updatedPackage.id);
      setDeleteConfirm({
        pkg: {
          id: updatedPackage.id,
          name: row?.name ?? selectedPackageForBudget?.name,
          autodesk_asset_id:
            row?.autodesk_asset_id ??
            selectedPackageForBudget?.autodesk_asset_id,
        },
        afterZeroBudget: true,
      });
    }
  };

  const columns = [
    {
      field: 'autodesk_name',
      headerName: 'Name',
      flex: 1.5,
      minWidth: 120,
    },
    {
      field: 'name',
      headerName: 'Description',
      flex: 2,
      minWidth: 150,
      editable: false,
    },
    {
      field: 'area',
      headerName: 'Area',
      flex: 1.5,
      minWidth: 120,
    },
    {
      field: 'trades',
      headerName: 'Trade',
      flex: 0.8,
      minWidth: 80,
    },
    {
      field: 'field_phase',
      headerName: 'Phase',
      flex: 0.8,
      minWidth: 80,
    },
    {
      field: 'field_cost_code',
      headerName: 'Cost Code',
      flex: 0.8,
      minWidth: 80,
    },
    {
      field: 'requires_shop_fabrication',
      headerName: 'Requires Shop F...',
      flex: 0.8,
      minWidth: 137,
      type: 'boolean',
    },
    {
      field: 'requires_vdc_design',
      headerName: 'Requires VDC D...',
      flex: 0.8,
      minWidth: 135,
      type: 'boolean',
    },
    {
      field: 'is_general_conditions',
      headerName: 'General Conditions',
      flex: 0.9,
      minWidth: 144,
      type: 'boolean',
    },
    {
      field: 'field_hrs_budgeted',
      headerName: 'Field Budget',
      flex: 0.9,
      minWidth: 90,
      type: 'number',
      valueFormatter: (params) =>
        params.value !== null && params.value !== undefined
          ? Number(params.value).toFixed(1)
          : '',
    },
    {
      field: 'shop_hrs_budgeted',
      headerName: 'Shop Budget',
      flex: 0.9,
      minWidth: 90,
      type: 'number',
      valueFormatter: (params) =>
        params.value !== null && params.value !== undefined
          ? Number(params.value).toFixed(1)
          : '',
    },
    {
      field: 'tracking_status',
      headerName: 'Status',
      flex: 1.5,
      minWidth: 120,
    },
    {
      field: 'archived',
      headerName: 'Archived',
      flex: 0.6,
      minWidth: 70,
      type: 'boolean',
    },
    {
      field: 'autodesk_delete_pending',
      headerName: '',
      flex: 1.0,
      minWidth: 140,
      valueFormatter: (value) => (value ? 'Deletion Blocked' : ''),
      renderCell: (params) =>
        params.value ? (
          <Tooltip title={params.row.autodesk_delete_pending_reason || ''}>
            <Chip
              label="Deletion Blocked"
              color="warning"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Tooltip>
        ) : (
          ''
        ),
    },
    {
      // Ticket #257: waiting for, or refused by, the background ACC asset run.
      field: 'acc_sync_state',
      headerName: '',
      flex: 0.9,
      minWidth: 110,
      valueFormatter: (params) =>
        params.value === 'failed'
          ? 'ACC failed'
          : params.value === 'pending'
            ? 'ACC pending'
            : '',
      renderCell: (params) =>
        params.value === 'failed' ? (
          <Tooltip title={stripAccPrefix(params.row.acc_sync_error)}>
            <Chip label="ACC failed" color="error" size="small" />
          </Tooltip>
        ) : params.value === 'pending' ? (
          <Tooltip title="Waiting for its ACC asset to be created">
            <Chip
              label="ACC pending"
              color="info"
              size="small"
              variant="outlined"
            />
          </Tooltip>
        ) : (
          ''
        ),
    },
    {
      field: 'actions',
      type: 'actions',
      width: 120,
      headerName: '',
      cellClassName: 'actions',
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            name="edit"
            label="Edit"
            onClick={() => {
              handleEditPackage(params);
            }}
          />,
          <GridActionsCellItem
            key="update-budget"
            icon={<AttachMoneyIcon />}
            name="update-budget"
            label="Update Budget"
            onClick={() => {
              handleOpenUpdateBudget(params);
            }}
          />,
        ];

        // If package is archived, show unarchive option
        if (params.row.archived) {
          actions.push(
            <GridActionsCellItem
              key="unarchive"
              icon={<UndoIcon />}
              name="unarchive"
              label="Unarchive"
              onClick={() => {
                handleUnarchivePackage(params.row);
              }}
            />
          );
        }

        // If deletion pending is true, show confirm/clear options instead of standard delete
        if (params.row.autodesk_delete_pending) {
          actions.push(
            <GridActionsCellItem
              key="confirm-delete"
              icon={<CheckIcon color="success" />}
              name="confirm-delete"
              label="Confirm Deletion"
              onClick={() => {
                handleOpenConfirmDeleteModal(params.row);
              }}
            />,
            <GridActionsCellItem
              key="clear-pending"
              icon={<UndoIcon color="primary" />}
              name="clear-pending"
              label="Clear Deletion Warning"
              onClick={() => {
                handleOpenClearPendingModal(params.row);
              }}
            />
          );
        } else {
          actions.push(
            <GridActionsCellItem
              key="delete"
              icon={<DeleteIcon />}
              name="delete"
              label="Delete"
              onClick={() => {
                handleDeletePackage(params);
              }}
            />
          );
        }

        return actions;
      },
    },
  ];

  if (!currentProject) {
    return <ProjectEmptyState />;
  }

  return (
    <div>
      <Box
        sx={{
          width: '75%',
          padding: 2,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
        }}
      >
        <Button
          sx={{ m: 1, height: 50 }}
          variant="contained"
          disabled={currentProject ? false : true}
          onClick={() => handleOpen()}
        >
          Add Package
        </Button>
        <Button
          sx={{ m: 1, height: 50 }}
          variant="outlined"
          endIcon={<ArrowDropDownIcon />}
          disabled={currentProject ? false : true}
          onClick={handleCsvMenuOpen}
        >
          CSV
        </Button>
        <Menu
          anchorEl={csvMenuAnchor}
          open={csvMenuOpen}
          onClose={handleCsvMenuClose}
        >
          <MenuItem onClick={handleDownloadTemplate}>
            Download Blank Template
          </MenuItem>
          <MenuItem
            onClick={handleDownloadExisting}
            disabled={packages.length === 0}
          >
            Download Existing Packages
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleUploadCsvClick}>Upload CSV</MenuItem>
        </Menu>
        <Button
          sx={{ m: 1, height: 50 }}
          variant="outlined"
          disabled={currentProject ? false : true}
          onClick={handleAreasOpen}
        >
          Edit Project Areas
        </Button>
        {auth.isAdmin && (
          <Tooltip
            title={
              otherSyncRunning
                ? 'Another ACC sync is running. Only one can run at a time — this button re-enables when it finishes.'
                : 'Syncs status step sets, statuses, categories, custom attributes and assets for this project from ACC. RFIs and submittals are not included — use the full sync on the Projects tab for those.'
            }
          >
            {/* span keeps the tooltip working while the button is disabled */}
            <span>
              <Button
                sx={{ m: 1, height: 50 }}
                variant="outlined"
                startIcon={<SyncIcon />}
                disabled={!currentProject || accSyncInFlight}
                onClick={handleSyncAssets}
              >
                {thisProjectSyncing ? 'Syncing…' : 'Sync Assets'}
              </Button>
            </span>
          </Tooltip>
        )}
        {auth.isAdmin &&
          currentProject &&
          (() => {
            const syncStatus = formatAssetSyncStatus();
            if (!syncStatus) return null;

            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: 320,
                }}
              >
                <Typography variant="caption" sx={{ color: syncStatus.color }}>
                  {syncStatus.text}
                </Typography>
              </Box>
            );
          })()}
        {/* Ticket #257: retry failed ACC assets and create any left pending
            (a stopped run, a server restart). */}
        {auth.isAdmin && (accAssetsWaiting > 0 || accAssetsRunning) && (
          <Tooltip
            title={
              accAssetsRunning
                ? 'ACC assets are being created in the background. This page updates when it finishes.'
                : 'Creates the ACC assets for packages marked "ACC pending", and tries the ones marked "ACC failed" again.'
            }
          >
            <span>
              <Button
                sx={{ m: 1, height: 50 }}
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                disabled={accAssetsRunning}
                onClick={handleCreatePendingAccAssets}
              >
                {accAssetsRunning
                  ? 'Creating ACC assets…'
                  : `Create pending ACC assets (${accAssetsWaiting})`}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          width: '98%',
          height: '75vh',
          // height: 771 9/5/24 this was set at a fixed height and then I increased the nav bar
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        <PackageModal
          packageSelected={currentPackage}
          editPackage={editPackage}
          open={open}
          handleCancel={handleCancel}
          handleAddPackage={handleAddPackage}
          handleUpdatePackage={handleUpdatePackage}
          currentProject={currentProject}
          onTradeAutoCopied={handleTradeAutoCopied}
          onActivitiesAutoCopied={handleActivitiesAutoCopied}
          onOpenUpdateBudget={handleOpenUpdateBudgetFromEditPackage}
        />

        <CsvPackageModal
          open={csvUploadOpen}
          onClose={handleCsvUploadClose}
          onUploadComplete={handleCsvUploadComplete}
          projectId={currentProject?.id}
        />

        {areasOpen && currentProject && (
          <Areas onClose={handleAreasClose} currentProject={currentProject} />
        )}

        <UpdateBudgetModal
          open={budgetModalOpen}
          packageSelected={selectedPackageForBudget}
          handleCancel={handleCloseUpdateBudget}
          handleUpdateBudgetComplete={handleUpdateBudgetComplete}
        />

        <DataGrid
          getRowId={(row) => row.id}
          columns={columns}
          rows={packages}
          components={{ Toolbar: GridToolbar }}
          autoPageSize
          componentsProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: true,
            },
          }}
          disableDensitySelector={true}
          disableColumnSelector={true}
          initialState={{
            filter: {
              filterModel: {
                items: [
                  {
                    columnField: 'archived',
                    operatorValue: 'is',
                    value: 'false',
                  },
                ],
              },
            },
          }}
        />
      </Box>

      {/* Autodesk Deletion Resolution Dialog */}
      <Dialog
        open={resolutionModal.open}
        onClose={handleCloseResolutionModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {resolutionModal.type === 'confirm'
            ? 'Confirm Autodesk Deletion'
            : 'Clear Deletion Warning'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {resolutionModal.type === 'confirm'
              ? `This package was deleted in Autodesk but contains charged hours. Confirming deletion will hide the package from the Kanban board, but preserve its timecard records.`
              : `This package was deleted in Autodesk but contains charged hours. Clearing the warning will keep the package active and prevent future syncs from auto-deleting it.`}
          </DialogContentText>
          {resolutionModal.packageData && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <strong>Package:</strong> {resolutionModal.packageData.name}
              <br />
              <strong>Warning:</strong>{' '}
              {resolutionModal.packageData.autodesk_delete_pending_reason}
            </Box>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Resolution Notes"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={resolutionModal.notes}
            onChange={(e) =>
              setResolutionModal((prev) => ({ ...prev, notes: e.target.value }))
            }
            placeholder="Provide a reason or context for this action..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResolutionModal}>Cancel</Button>
          <Button
            onClick={handleResolveDeletion}
            variant="contained"
            color={resolutionModal.type === 'confirm' ? 'error' : 'primary'}
            disabled={!resolutionModal.notes.trim()}
          >
            {resolutionModal.type === 'confirm'
              ? 'Confirm Deletion'
              : 'Clear Warning'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={
          snackbar.autoHideDuration ||
          (snackbar.severity === 'error' ? 6000 : 3000)
        }
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Autodesk Sync Warning Dialog */}
      <Dialog
        open={autodeskSyncWarningOpen}
        onClose={() => setAutodeskSyncWarningOpen(false)}
        aria-labelledby="autodesk-sync-warning-title"
        aria-describedby="autodesk-sync-warning-description"
      >
        <DialogTitle id="autodesk-sync-warning-title">
          Autodesk Sync Warning
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="autodesk-sync-warning-description">
            {autodeskSyncWarningMessage ||
              'Your changes were saved, but we were unable to sync them back to ' +
                'Autodesk Construction Cloud. If an ACC sync runs before this is ' +
                'resolved, these values may be overwritten. Please notify an ' +
                'administrator of this failure and indicate the project, package ' +
                'name, and field that was updated.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutodeskSyncWarningOpen(false)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm: the Delete action, or "delete now?" after Update
          Budget brings the budget to zero */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        aria-labelledby="delete-package-title"
        aria-describedby="delete-package-description"
      >
        <DialogTitle id="delete-package-title">Delete package</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-package-description">
            {deleteConfirm?.afterZeroBudget
              ? `The budget for "${deleteConfirm?.pkg.name}" is now zero. Delete this package now?`
              : `Are you sure you want to delete "${deleteConfirm?.pkg.name}"?`}{' '}
            {accArchiveNote(deleteConfirm?.pkg)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} autoFocus>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
